import { SvgIcon, Box } from '@mui/material';
import React from 'react';
import { IconProps, IconSize } from './Icon.props';
import { ResponsiveSize } from '@/types/common';

interface IconSizeConfig {
  container: number | ResponsiveSize<number>;
  icon: number | string | ResponsiveSize<number | string>;
  badge: number | ResponsiveSize<number>;
  badgePosition: {
    top: string | number | ResponsiveSize<string | number>;
    right: string | number | ResponsiveSize<string | number>;
  };
}

const sizeVariants: Record<Exclude<IconSize, 'custom'>, IconSizeConfig> = {
  xs: {
    container: 24,
    icon: 16,
    badge: 8,
    badgePosition: { top: '5%', right: '5%' },
  },
  s: {
    container: 32,
    icon: 25,
    badge: 10,
    badgePosition: { top: '0%', right: '0%' },
  },
  m: {
    container: { xs: 40, sm: 48, md: 56 },
    icon: { xs: '28px', sm: '34px', md: '40px' },
    badge: { xs: 10, sm: 12, md: 14 },
    badgePosition: {
      top: { xs: '8%', sm: '10%', md: '12%' },
      right: { xs: '8%', sm: '10%', md: '12%' },
    },
  },
  l: {
    container: { xs: 60, sm: 70, md: 80 },
    icon: { xs: '32px', sm: '38px', md: '44px' },
    badge: { xs: 12, sm: 14, md: 16 },
    badgePosition: {
      top: { xs: '10%', sm: '12%', md: '15%' },
      right: { xs: '10%', sm: '12%', md: '15%' },
    },
  },
  xl: {
    container: { xs: 80, sm: 96, md: 112 },
    icon: { xs: '48px', sm: '60px', md: '72px' },
    badge: { xs: 16, sm: 20, md: 24 },
    badgePosition: {
      top: { xs: '12%', sm: '15%', md: '18%' },
      right: { xs: '12%', sm: '15%', md: '18%' },
    },
  },
};

export const Icon = ({
  icon: IconComponent,
  size,
  containerSize,
  iconSize,
  badgeSizeCustom,
  badgePositionCustom,
  iconColor,
  src,
  alt = 'icon',
  badgeIcon,
  badgeSize = 10,
  badgePosition = { top: '0%', right: '0%' },
  badgeAlt = 'Badge Icon',
  showBadge = true,
  badgeSx,
  containerSx,
  iconSx,
  children,
  sx,
  ...props
}: IconProps) => {
  // Determine if we're using size variants or custom sizing
  const usingSizeVariants = size && size !== 'custom';

  // Get size configuration
  const sizeConfig = usingSizeVariants
    ? sizeVariants[size as Exclude<IconSize, 'custom'>]
    : size === 'custom'
      ? {
          container: containerSize || 32,
          icon: iconSize || 25,
          badge: badgeSizeCustom || 10,
          badgePosition: badgePositionCustom || { top: '0%', right: '0%' },
        }
      : null;

  // Helper function to render the primary icon
  const renderPrimaryIcon = () => {
    // If using size variants, render IconComponent with sizing
    if (sizeConfig && IconComponent) {
      const combinedSx = {
        fontSize: sizeConfig.icon,
        width: sizeConfig.icon,
        height: sizeConfig.icon,
        color: iconColor,
        ...(iconSx || {}),
        ...(sx || {}),
      };
      return <Box component={IconComponent} sx={combinedSx as any} />;
    }

    // If src is provided, render as img element
    if (src) {
      const combinedSx = {
        width: '1em',
        height: '1em',
        display: 'inline-block',
        fontSize: 'inherit',
        ...(iconSx || {}),
        ...(sx || {}),
      };
      return <Box component="img" src={src} alt={alt} sx={combinedSx as any} />;
    }

    // If icon component is provided, render as SvgIcon
    if (IconComponent) {
      const combinedSx = { ...(iconSx || {}), ...(sx || {}) };
      return <SvgIcon component={IconComponent} sx={combinedSx} {...props} />;
    }

    // Default: render SvgIcon with children
    const combinedSx = { ...(iconSx || {}), ...(sx || {}) };
    return (
      <SvgIcon sx={combinedSx} {...props}>
        {children}
      </SvgIcon>
    );
  };

  // Helper function to render the badge icon
  const renderBadgeIcon = () => {
    if (!badgeIcon || !showBadge) return null;

    const isSvgPath = typeof badgeIcon === 'string';

    // Get badge size and position from size config or props
    const finalBadgeSize = sizeConfig ? sizeConfig.badge : badgeSize;
    const finalBadgePosition = sizeConfig
      ? sizeConfig.badgePosition
      : badgePosition;

    return (
      <Box
        sx={
          {
            position: 'absolute',
            top: finalBadgePosition.top,
            right: finalBadgePosition.right,
            bottom: badgePosition.bottom,
            left: badgePosition.left,
            width: finalBadgeSize,
            height: finalBadgeSize,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            ...badgeSx,
          } as any
        }
      >
        {isSvgPath ? (
          // Render as img element for SVG paths
          <Box
            component="img"
            src={badgeIcon}
            alt={badgeAlt}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        ) : (
          // Render as React component for icon components
          React.createElement(badgeIcon as React.ElementType, {
            sx: {
              width: '100%',
              height: '100%',
              fontSize: finalBadgeSize,
            },
          })
        )}
      </Box>
    );
  };

  // If using size variants, render with container
  if (sizeConfig) {
    return (
      <Box
        sx={
          {
            width: sizeConfig.container,
            height: sizeConfig.container,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'visible',
            flexShrink: 0,
            ...containerSx,
          } as any
        }
      >
        {renderPrimaryIcon()}
        {renderBadgeIcon()}
      </Box>
    );
  }

  // If badge icon is provided, wrap in container with relative positioning
  if (badgeIcon) {
    return (
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...containerSx,
        }}
      >
        {renderPrimaryIcon()}
        {renderBadgeIcon()}
      </Box>
    );
  }

  // No badge - render primary icon only
  return renderPrimaryIcon();
};

export default Icon;
